import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  Loader2,
  ChevronDown,
  ChevronUp,
  GripVertical,
  BookOpen,
  Video,
  FileText,
  Eye,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface Lesson {
  id?: string;
  title: string;
  type: 'video' | 'text' | 'quiz' | 'assignment';
  description: string;
  content: string;
  video_url: string;
  duration_minutes: string;
  is_preview: boolean;
  display_order: number;
}

interface Module {
  id?: string;
  title: string;
  description: string;
  unlock_delay_days: string;
  display_order: number;
  lessons: Lesson[];
  isExpanded: boolean;
}

interface CourseFormData {
  name: string;
  slug: string;
  short_description: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  price: string;
  compare_at_price: string;
  access_type: 'lifetime' | 'limited' | 'subscription';
  time_limit_days: string;
  status: 'draft' | 'published' | 'archived';
  is_featured: boolean;
  thumbnail_url: string;
  meta_title: string;
  meta_description: string;
}

const INITIAL_FORM: CourseFormData = {
  name: '',
  slug: '',
  short_description: '',
  description: '',
  level: 'beginner',
  price: '',
  compare_at_price: '',
  access_type: 'lifetime',
  time_limit_days: '',
  status: 'draft',
  is_featured: false,
  thumbnail_url: '',
  meta_title: '',
  meta_description: '',
};

const INITIAL_LESSON: Lesson = {
  title: '',
  type: 'video',
  description: '',
  content: '',
  video_url: '',
  duration_minutes: '',
  is_preview: false,
  display_order: 0,
};

const CourseEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<CourseFormData>(INITIAL_FORM);
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditMode) {
      fetchCourse();
    }
  }, [id]);

  async function fetchCourse() {
    try {
      setIsLoading(true);
      const response = await api.get<any>(`/admin/courses/${id}`);
      const course = response.course;
      setFormData({
        name: course.name || '',
        slug: course.slug || '',
        short_description: course.short_description || '',
        description: course.description || '',
        level: course.level || 'beginner',
        price: course.price?.toString() || '',
        compare_at_price: course.compare_at_price?.toString() || '',
        access_type: course.access_type || 'lifetime',
        time_limit_days: course.time_limit_days?.toString() || '',
        status: course.status || 'draft',
        is_featured: course.is_featured || false,
        thumbnail_url: course.thumbnail_url || '',
        meta_title: course.meta_title || '',
        meta_description: course.meta_description || '',
      });
      setModules(
        (course.modules || []).map((m: any) => ({
          ...m,
          unlock_delay_days: m.unlock_delay_days?.toString() || '0',
          isExpanded: false,
          lessons: (m.lessons || []).map((l: any) => ({
            ...l,
            duration_minutes: l.duration_minutes?.toString() || '',
            content: l.content || '',
            video_url: l.video_url || '',
            description: l.description || '',
          })),
        }))
      );
    } catch (err) {
      useUIStore.getState().addToast('Failed to load course', 'error');
      navigate('/admin/courses');
    } finally {
      setIsLoading(false);
    }
  }

  function updateField<K extends keyof CourseFormData>(key: K, value: CourseFormData[K]) {
    setFormData((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'name' && !isEditMode) {
        updated.slug = (value as string)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }
      return updated;
    });
  }

  // Module management
  function addModule() {
    setModules((prev) => [
      ...prev,
      {
        title: `Module ${prev.length + 1}`,
        description: '',
        unlock_delay_days: '0',
        display_order: prev.length,
        lessons: [],
        isExpanded: true,
      },
    ]);
  }

  function updateModule(index: number, field: keyof Module, value: any) {
    setModules((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  }

  function removeModule(index: number) {
    setModules((prev) => prev.filter((_, i) => i !== index).map((m, i) => ({ ...m, display_order: i })));
  }

  function moveModule(index: number, direction: 'up' | 'down') {
    setModules((prev) => {
      const arr = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= arr.length) return prev;
      [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
      return arr.map((m, i) => ({ ...m, display_order: i }));
    });
  }

  // Lesson management
  function addLesson(moduleIndex: number) {
    setModules((prev) =>
      prev.map((m, i) =>
        i === moduleIndex
          ? {
              ...m,
              lessons: [...m.lessons, { ...INITIAL_LESSON, display_order: m.lessons.length }],
            }
          : m
      )
    );
  }

  function updateLesson(moduleIndex: number, lessonIndex: number, field: keyof Lesson, value: any) {
    setModules((prev) =>
      prev.map((m, mi) =>
        mi === moduleIndex
          ? {
              ...m,
              lessons: m.lessons.map((l, li) =>
                li === lessonIndex ? { ...l, [field]: value } : l
              ),
            }
          : m
      )
    );
  }

  function removeLesson(moduleIndex: number, lessonIndex: number) {
    setModules((prev) =>
      prev.map((m, mi) =>
        mi === moduleIndex
          ? {
              ...m,
              lessons: m.lessons
                .filter((_, li) => li !== lessonIndex)
                .map((l, i) => ({ ...l, display_order: i })),
            }
          : m
      )
    );
  }

  function moveLesson(moduleIndex: number, lessonIndex: number, direction: 'up' | 'down') {
    setModules((prev) =>
      prev.map((m, mi) => {
        if (mi !== moduleIndex) return m;
        const lessons = [...m.lessons];
        const targetIndex = direction === 'up' ? lessonIndex - 1 : lessonIndex + 1;
        if (targetIndex < 0 || targetIndex >= lessons.length) return m;
        [lessons[lessonIndex], lessons[targetIndex]] = [lessons[targetIndex], lessons[lessonIndex]];
        return { ...m, lessons: lessons.map((l, i) => ({ ...l, display_order: i })) };
      })
    );
  }

  // Thumbnail upload
  async function handleThumbnailUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingThumbnail(true);
      const fd = new FormData();
      fd.append('file', file);
      // `/admin/upload/cms` (singular) is the mounted route — see functions/src/index.ts.
      // width-only caps the long edge at 1200 and preserves the designer's aspect
      // ratio; the route re-encodes to WebP and stores under the public `cms/` prefix.
      const response = await api.upload<{ url: string }>('/admin/upload/cms?width=1200', fd);
      updateField('thumbnail_url', response.url);
      useUIStore.getState().addToast('Thumbnail uploaded', 'success');
    } catch {
      useUIStore.getState().addToast('Failed to upload thumbnail', 'error');
    } finally {
      setIsUploadingThumbnail(false);
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
    }
  }

  // Video upload
  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>, moduleIndex: number, lessonIndex: number) {
    const file = e.target.files?.[0];
    if (!file) return;

    const key = `${moduleIndex}-${lessonIndex}`;

    // There is no generic video upload route. Video lands via the course route,
    // which writes `lessons.video_key`/`video_url` itself — so it needs the
    // persisted course, module and lesson ids, not array indices. An unsaved
    // module or lesson has no id yet.
    const moduleId = modules[moduleIndex]?.id;
    const lessonId = modules[moduleIndex]?.lessons[lessonIndex]?.id;
    if (!id || !moduleId || !lessonId) {
      useUIStore
        .getState()
        .addToast('Save the course first — a video attaches to a saved lesson', 'error');
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }

    try {
      setUploadingVideo(key);
      const fd = new FormData();
      fd.append('file', file);
      const response = await api.upload<{ video_url: string }>(
        `/admin/courses/${id}/modules/${moduleId}/lessons/${lessonId}/upload-video`,
        fd,
      );
      updateLesson(moduleIndex, lessonIndex, 'video_url', response.video_url);
      useUIStore.getState().addToast('Video uploaded', 'success');
    } catch {
      useUIStore.getState().addToast('Failed to upload video', 'error');
    } finally {
      setUploadingVideo(null);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  }

  async function handleSave() {
    if (!formData.name.trim()) {
      useUIStore.getState().addToast('Course name is required', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        compare_at_price: formData.compare_at_price ? parseFloat(formData.compare_at_price) : null,
        time_limit_days: formData.access_type === 'limited' && formData.time_limit_days
          ? parseInt(formData.time_limit_days)
          : null,
        modules: modules.map(({ isExpanded, ...m }) => ({
          ...m,
          unlock_delay_days: parseInt(m.unlock_delay_days) || 0,
          lessons: m.lessons.map((l) => ({
            ...l,
            duration_minutes: l.duration_minutes ? parseInt(l.duration_minutes) : null,
          })),
        })),
      };

      if (isEditMode) {
        await api.put(`/admin/courses/${id}`, payload);
        useUIStore.getState().addToast('Course updated successfully', 'success');
      } else {
        const response = await api.post<{ course: { id: string } }>('/admin/courses', payload);
        useUIStore.getState().addToast('Course created successfully', 'success');
        navigate(`/admin/courses/${response.course.id}`);
      }
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to save course', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    try {
      setIsDeleting(true);
      await api.delete(`/admin/courses/${id}`);
      useUIStore.getState().addToast('Course deleted', 'success');
      navigate('/admin/courses');
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to delete course', 'error');
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/courses">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Course' : 'New Course'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {isEditMode && (
            <Button variant="danger" size="sm" onClick={() => setDeleteModalOpen(true)}>
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          )}
          <Button variant="primary" size="md" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isSaving ? 'Saving...' : 'Save Course'}
          </Button>
        </div>
      </div>

      {/* Basic Info */}
      <Card padding="lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Course Name"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., Getting Started"
            />
            <Input
              label="Slug"
              value={formData.slug}
              onChange={(e) => updateField('slug', e.target.value)}
              placeholder="getting-started"
            />
          </div>
          <Input
            label="Short Description"
            value={formData.short_description}
            onChange={(e) => updateField('short_description', e.target.value)}
            placeholder="A brief summary for course listings"
          />
          <Textarea
            label="Full Description"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={6}
            placeholder="Detailed course description..."
          />
        </div>
      </Card>

      {/* Pricing & Settings */}
      <Card padding="lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Settings</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Select
            label="Level"
            value={formData.level}
            onChange={(e) => updateField('level', e.target.value as any)}
            options={[
              { label: 'Beginner', value: 'beginner' },
              { label: 'Intermediate', value: 'intermediate' },
              { label: 'Advanced', value: 'advanced' },
            ]}
          />
          <Input
            label="Price"
            type="number"
            value={formData.price}
            onChange={(e) => updateField('price', e.target.value)}
            placeholder="0.00"
          />
          <Input
            label="Compare at Price"
            type="number"
            value={formData.compare_at_price}
            onChange={(e) => updateField('compare_at_price', e.target.value)}
            placeholder="0.00"
          />
          <Select
            label="Access Type"
            value={formData.access_type}
            onChange={(e) => updateField('access_type', e.target.value as any)}
            options={[
              { label: 'Lifetime', value: 'lifetime' },
              { label: 'Limited Time', value: 'limited' },
              { label: 'Subscription', value: 'subscription' },
            ]}
          />
          {formData.access_type === 'limited' && (
            <Input
              label="Time Limit (days)"
              type="number"
              value={formData.time_limit_days}
              onChange={(e) => updateField('time_limit_days', e.target.value)}
              placeholder="365"
            />
          )}
          <Select
            label="Status"
            value={formData.status}
            onChange={(e) => updateField('status', e.target.value as any)}
            options={[
              { label: 'Draft', value: 'draft' },
              { label: 'Published', value: 'published' },
              { label: 'Archived', value: 'archived' },
            ]}
          />
        </div>
        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => updateField('is_featured', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Featured course</span>
          </label>
        </div>
      </Card>

      {/* Thumbnail */}
      <Card padding="lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Thumbnail</h2>
        <div className="flex items-start gap-4">
          {formData.thumbnail_url ? (
            <div className="relative">
              <img src={formData.thumbnail_url} alt="Thumbnail" className="w-40 h-24 object-cover rounded-lg" />
              <button
                onClick={() => updateField('thumbnail_url', '')}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
              >
                &times;
              </button>
            </div>
          ) : (
            <div className="w-40 h-24 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              className="hidden"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => thumbnailInputRef.current?.click()}
              disabled={isUploadingThumbnail}
            >
              {isUploadingThumbnail ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              Upload Thumbnail
            </Button>
            <p className="text-xs text-gray-500 mt-1">Recommended: 1280x720px</p>
          </div>
        </div>
      </Card>

      {/* SEO */}
      <Card padding="lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">SEO</h2>
        <div className="space-y-4">
          <Input
            label="Meta Title"
            value={formData.meta_title}
            onChange={(e) => updateField('meta_title', e.target.value)}
            placeholder="Page title for search engines"
          />
          <Textarea
            label="Meta Description"
            value={formData.meta_description}
            onChange={(e) => updateField('meta_description', e.target.value)}
            rows={3}
            placeholder="Description for search engine results"
          />
        </div>
      </Card>

      {/* Modules & Lessons */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Modules & Lessons</h2>
          <Button variant="primary" size="sm" onClick={addModule}>
            <Plus className="w-4 h-4 mr-1" />
            Add Module
          </Button>
        </div>

        {modules.length === 0 && (
          <Card padding="lg">
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600 mb-4">No modules yet. Add a module to start building your curriculum.</p>
              <Button variant="primary" size="sm" onClick={addModule}>
                <Plus className="w-4 h-4 mr-1" />
                Add First Module
              </Button>
            </div>
          </Card>
        )}

        {modules.map((module, moduleIndex) => (
          <Card key={moduleIndex} padding="none">
            {/* Module Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
              <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <button
                onClick={() => updateModule(moduleIndex, 'isExpanded', !module.isExpanded)}
                className="flex-1 flex items-center gap-2 text-left"
              >
                {module.isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
                <span className="font-medium text-gray-900">
                  {module.title || `Module ${moduleIndex + 1}`}
                </span>
                <Badge variant="gray">{module.lessons.length} lessons</Badge>
              </button>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => moveModule(moduleIndex, 'up')}
                  disabled={moduleIndex === 0}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => moveModule(moduleIndex, 'down')}
                  disabled={moduleIndex === modules.length - 1}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeModule(moduleIndex)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>

            {/* Module Body */}
            {module.isExpanded && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Module Title"
                    value={module.title}
                    onChange={(e) => updateModule(moduleIndex, 'title', e.target.value)}
                    placeholder="Module title"
                  />
                  <Input
                    label="Unlock Delay (days)"
                    type="number"
                    value={module.unlock_delay_days}
                    onChange={(e) => updateModule(moduleIndex, 'unlock_delay_days', e.target.value)}
                    placeholder="0"
                    helperText="Days after enrollment before this module unlocks"
                  />
                </div>
                <Textarea
                  label="Module Description"
                  value={module.description}
                  onChange={(e) => updateModule(moduleIndex, 'description', e.target.value)}
                  rows={2}
                  placeholder="Brief description of this module"
                />

                {/* Lessons */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-700">Lessons</h4>
                    <Button variant="secondary" size="sm" onClick={() => addLesson(moduleIndex)}>
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Lesson
                    </Button>
                  </div>

                  {module.lessons.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No lessons in this module yet.
                    </p>
                  )}

                  {module.lessons.map((lesson, lessonIndex) => (
                    <div
                      key={lessonIndex}
                      className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {lesson.type === 'video' ? (
                            <Video className="w-4 h-4 text-blue-500" />
                          ) : lesson.type === 'text' ? (
                            <FileText className="w-4 h-4 text-green-500" />
                          ) : (
                            <BookOpen className="w-4 h-4 text-purple-500" />
                          )}
                          <span className="text-sm font-medium text-gray-700">
                            Lesson {lessonIndex + 1}
                          </span>
                          {lesson.is_preview && <Badge variant="blue">Preview</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveLesson(moduleIndex, lessonIndex, 'up')}
                            disabled={lessonIndex === 0}
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveLesson(moduleIndex, lessonIndex, 'down')}
                            disabled={lessonIndex === module.lessons.length - 1}
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLesson(moduleIndex, lessonIndex)}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="Lesson Title"
                          value={lesson.title}
                          onChange={(e) =>
                            updateLesson(moduleIndex, lessonIndex, 'title', e.target.value)
                          }
                          placeholder="Lesson title"
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Select
                            label="Type"
                            value={lesson.type}
                            onChange={(e) =>
                              updateLesson(moduleIndex, lessonIndex, 'type', e.target.value)
                            }
                            options={[
                              { label: 'Video', value: 'video' },
                              { label: 'Text', value: 'text' },
                              { label: 'Quiz', value: 'quiz' },
                              { label: 'Assignment', value: 'assignment' },
                            ]}
                          />
                          <Input
                            label="Duration (min)"
                            type="number"
                            value={lesson.duration_minutes}
                            onChange={(e) =>
                              updateLesson(moduleIndex, lessonIndex, 'duration_minutes', e.target.value)
                            }
                            placeholder="0"
                          />
                        </div>
                      </div>

                      <Textarea
                        label="Description"
                        value={lesson.description}
                        onChange={(e) =>
                          updateLesson(moduleIndex, lessonIndex, 'description', e.target.value)
                        }
                        rows={2}
                        placeholder="Brief lesson description"
                      />

                      {lesson.type === 'text' && (
                        <Textarea
                          label="Content"
                          value={lesson.content}
                          onChange={(e) =>
                            updateLesson(moduleIndex, lessonIndex, 'content', e.target.value)
                          }
                          rows={6}
                          placeholder="Lesson content..."
                        />
                      )}

                      {lesson.type === 'video' && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-stone-700">Video</label>
                          {lesson.video_url ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-green-600 truncate flex-1">
                                {lesson.video_url}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  updateLesson(moduleIndex, lessonIndex, 'video_url', '')
                                }
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/*"
                                onChange={(e) => handleVideoUpload(e, moduleIndex, lessonIndex)}
                                className="hidden"
                              />
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => videoInputRef.current?.click()}
                                disabled={uploadingVideo === `${moduleIndex}-${lessonIndex}`}
                              >
                                {uploadingVideo === `${moduleIndex}-${lessonIndex}` ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-1" />
                                )}
                                Upload Video
                              </Button>
                              <span className="text-xs text-gray-500 ml-2">Or paste URL:</span>
                              <Input
                                className="mt-1"
                                value={lesson.video_url}
                                onChange={(e) =>
                                  updateLesson(moduleIndex, lessonIndex, 'video_url', e.target.value)
                                }
                                placeholder="https://..."
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={lesson.is_preview}
                          onChange={(e) =>
                            updateLesson(moduleIndex, lessonIndex, 'is_preview', e.target.checked)
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Eye className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-sm text-gray-700">Free preview lesson</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete Course">
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete this course? This action cannot be undone and will remove
            all modules, lessons, and enrollment data.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isDeleting ? 'Deleting...' : 'Delete Course'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseEditor;
